import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage'
import { storage } from '../firebase'

export default function Downloads() {
  const { userClaims, hasPermission, PERMISSIONS } = useAuth()
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [customerName, setCustomerName] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')

  useEffect(() => {
    if (userClaims?.customer) {
      setCustomerName(userClaims.customer)
      if (hasPermission(PERMISSIONS.DOWNLOADS.ACCESS)) {
        fetchReports(userClaims.customer)
      }
    }
  }, [userClaims])

  const fetchReports = async (customer) => {
    if (!customer) {
      setError('No customer name found in claims')
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log(`Fetching reports for customer: ${customer}`)
      
      // Create reference to customer's monthly-report folder in Cloud Storage
      const monthlyReportFolderRef = ref(storage, `${customer}/monthly-report`)
      
      // List all files in the customer's monthly-report folder
      const listResult = await listAll(monthlyReportFolderRef)
      
      const reportPromises = listResult.items
        .filter(item => item.name.toLowerCase().endsWith('.csv'))
        .map(async (item) => {
          try {
            const [downloadURL, metadata] = await Promise.all([
              getDownloadURL(item),
              getMetadata(item)
            ])

            // Parse filename to extract date information
            const filename = item.name
            const filenameParts = filename.replace('.csv', '').split('_')
            
            return {
              name: filename,
              fullPath: item.fullPath,
              downloadURL,
              size: metadata.size,
              timeCreated: metadata.timeCreated,
              updated: metadata.updated,
              customMetadata: metadata.customMetadata || {},
              // Try to extract month/year from filename
              parsedDate: parseReportDate(filename)
            }
          } catch (itemError) {
            console.error(`Error processing file ${item.name}:`, itemError)
            return null
          }
        })

      const resolvedReports = await Promise.all(reportPromises)
      const validReports = resolvedReports.filter(report => report !== null)

      setReports(validReports)
      console.log(`Found ${validReports.length} CSV reports for customer: ${customer}`)
    } catch (err) {
      console.error('Error fetching reports:', err)
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        customData: err.customData,
        serverResponse: err.serverResponse
      })
      let errorMessage = `Failed to fetch reports: ${err.message}`
      
      if (err.code === 'permission-denied') {
        errorMessage = 'Permission denied: Unable to access Cloud Storage. Please check your Firebase authentication and Cloud Storage rules.'
      } else if (err.code === 'storage/object-not-found') {
        errorMessage = `Cloud Storage folder "${customer}/monthly-report/" not found. Please ensure the folder exists and contains CSV files.`
      } else if (err.code === 'storage/unauthorized') {
        errorMessage = 'Unauthorized access to Cloud Storage. Please verify your Firebase authentication.'
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const parseReportDate = (filename) => {
    // Try different date formats commonly used in filenames
    const patterns = [
      /(\w+)-(\d{4})/, // Month-YYYY (e.g., April-2023.csv)
      /(\d{4})-(\d{2})/, // YYYY-MM
      /(\d{2})-(\d{4})/, // MM-YYYY
      /(\d{4})(\d{2})/, // YYYYMM
      /report_(\d{4})_(\d{2})/, // report_YYYY_MM
      /(\w+)_(\d{4})/ // Month_YYYY
    ]

    for (const pattern of patterns) {
      const match = filename.match(pattern)
      if (match) {
        let year, month
        if (pattern.source.includes('(\\w+)')) {
          // Month name format (e.g., April-2023 or Month_YYYY)
          const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
                            'july', 'august', 'september', 'october', 'november', 'december']
          month = monthNames.indexOf(match[1].toLowerCase()) + 1
          year = parseInt(match[2])
        } else {
          year = parseInt(match[1])
          month = parseInt(match[2])
          
          // Handle MM-YYYY vs YYYY-MM
          if (year < 100) {
            [year, month] = [month, year]
          }
        }
        
        if (year > 2000 && month >= 1 && month <= 12) {
          return new Date(year, month - 1, 1)
        }
      }
    }
    
    return null
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (date) => {
    if (!date) return 'Unknown'
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getReportMonth = (report) => {
    if (report.parsedDate) {
      return report.parsedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      })
    }
    return 'Unknown Period'
  }

  const handleDownload = async (report) => {
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a')
      link.href = report.downloadURL
      link.download = report.name
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      console.log(`Downloaded report: ${report.name}`)
    } catch (err) {
      console.error('Error downloading report:', err)
      setError(`Failed to download report: ${err.message}`)
    }
  }

  const handleRefresh = () => {
    if (customerName) {
      fetchReports(customerName)
    }
  }

  // Filter and sort reports
  const filteredReports = reports
    .filter(report => 
      report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getReportMonth(report).toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue, bValue
      
      switch (sortBy) {
        case 'date':
          aValue = a.parsedDate || new Date(a.timeCreated)
          bValue = b.parsedDate || new Date(b.timeCreated)
          break
        case 'name':
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case 'size':
          aValue = a.size
          bValue = b.size
          break
        default:
          aValue = a.timeCreated
          bValue = b.timeCreated
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

  if (!userClaims) {
    return (
      <div className="alert alert-info">
        <i className="fas fa-info-circle me-2"></i>
        Loading user claims...
      </div>
    )
  }

  if (!hasPermission(PERMISSIONS.DOWNLOADS.ACCESS)) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-lock me-2"></i>
        You don't have permission to access Downloads.
      </div>
    )
  }

  if (!customerName) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle me-2"></i>
        No customer name found in Firebase claims. Expected claim: "customer"
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">Downloads</h5>
          <small className="text-muted">Monthly CSV reports</small>
        </div>
        <button 
          className="btn btn-outline-primary btn-sm"
          onClick={handleRefresh}
          disabled={loading}
        >
          <i className="fas fa-sync-alt me-1"></i>
          Refresh
        </button>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Error:</strong> {error}
          <br />
          <small>
            <strong>Troubleshooting:</strong>
            <ul className="mb-0 mt-2">
              <li>Ensure Cloud Storage permissions are configured correctly</li>
              <li>Check that monthly report files exist in the expected location</li>
              <li>Verify your authentication and access permissions</li>
            </ul>
          </small>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search reports by filename or month..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="size">Sort by Size</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Loading reports from Cloud Storage...</div>
        </div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white">
            <h6 className="mb-0">
              Available Reports ({filteredReports.length})
              {filteredReports.length > 0 && (
                <span className="badge bg-primary ms-2">{customerName}</span>
              )}
            </h6>
          </div>
          <div className="card-body p-0">
            {filteredReports.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-file-csv fa-3x mb-3 d-block opacity-50"></i>
                <p>No CSV reports found</p>
                <small>
                  {reports.length === 0 
                    ? 'No monthly reports found for this customer'
                    : 'No reports match your search criteria'
                  }
                </small>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Report Period</th>
                      <th>Filename</th>
                      <th>File Size</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReports.map((report, index) => (
                      <tr key={report.fullPath || index}>
                        <td>
                          <strong>{getReportMonth(report)}</strong>
                          {!report.parsedDate && (
                            <small className="text-muted d-block">Date not parsed</small>
                          )}
                        </td>
                        <td>
                          <code className="small">{report.name}</code>
                        </td>
                        <td>{formatFileSize(report.size)}</td>
                        <td>
                          <small>{formatDate(report.timeCreated)}</small>
                        </td>
                        <td>
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => handleDownload(report)}
                            title="Download Report"
                          >
                            <i className="fas fa-download me-1"></i>
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}